import { SunfireRowFull, normalizePhone } from './csvParser';

export interface RecordingRecord {
  PK: string;
  SK: string;
  s3Key: string;
  originalFilename: string;
  recordDate: string;
  agentId: string;
  agentEmail?: string;
  duration: number;
  systemId: string;
  callerId: string;
  direction?: string;
  campaign?: string;
  callTimestamp?: string;   // NEW — orders calls + shown in the modal
  archivePath?: string;     // NEW — lets AudioPlayer resolve the S3 key
}

export interface SmsMatchedPair {
  csvRow: SunfireRowFull;
  recording: RecordingRecord;
  matchType: 'auto' | 'manual';
}


export interface MultiCallGroup {
  csvRow: SunfireRowFull;
  candidates: RecordingRecord[]; // all matches for this row, sorted by call time ascending
}

export interface SmsMatchPlan {
  auto: SmsMatchedPair[];            // rows with exactly one matching recording
  groups: MultiCallGroup[];          // rows with more than one matching recording
  unmatchedCsv: SunfireRowFull[];    // rows with zero matching recordings
  unmatchedRecordings: RecordingRecord[];
}

export interface SmsExportItem {
  PK: string; SK: string; s3Key: string;
  csvData: Record<string, string>;
  callIdSuffix: string; // '' for single; '-1' | '-2' | … when an enrollment sends multiple
}

function extractPhoneFromRecording(rec: RecordingRecord): string {
  // Find the caller's phone among the underscore-delimited filename fields.
  // Different systems put the phone in different positions: DialedIn (post
  // zip-ingest) prefixes the filename with a list id, campaign, and agent email
  // BEFORE the phone (e.g. "225687_Medicare Inbounds_agent@x.com_9542358169_..."),
  // so assuming it is the first field returns the list id and never matches.
  // Scan for the first field that is a 10-digit (or 1+10) phone number; skip
  // email fields. Backward-compatible with phone-first filenames.
  const filename = rec.originalFilename || '';
  for (const field of filename.split('_')) {
    if (field.includes('@')) continue;
    const digits = field.replace(/\D/g, '');
    if (digits.length === 10 || (digits.length === 11 && digits[0] === '1')) {
      return digits.slice(-10);
    }
  }
  return '';
}

export function buildExportItems(pairs: SmsMatchedPair[]): SmsExportItem[] {
  // Group by enrollment (ConfirmationNumber) to decide CallID suffixes. Empty
  // confirmation numbers are never grouped (would merge unrelated rows).
  const groups = new Map<string, SmsMatchedPair[]>();
  const singletons: SmsMatchedPair[] = [];
  for (const pair of pairs) {
    const key = pair.csvRow.confirmationNumber || '';
    if (!key) { singletons.push(pair); continue; }
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(pair);
  }

  const toItem = (pair: SmsMatchedPair, suffix: string): SmsExportItem => ({
    PK: pair.recording.PK,
    SK: pair.recording.SK,
    s3Key: pair.recording.s3Key,
    csvData: { ...pair.csvRow.rawFields, ApplicantPhone: pair.csvRow.applicantPhone },
    callIdSuffix: suffix,
  });

  const items: SmsExportItem[] = [];
  for (const pair of singletons) items.push(toItem(pair, ''));
  Array.from(groups.values()).forEach((group) => {
    if (group.length === 1) { items.push(toItem(group[0], '')); return; }
    const ordered = [...group].sort((a, b) =>
      recordingSortKey(a.recording).localeCompare(recordingSortKey(b.recording)));
    ordered.forEach((pair, idx) => items.push(toItem(pair, `-${idx + 1}`)));
  });
  return items;
}

function recordingSortKey(rec: RecordingRecord): string {
  return rec.callTimestamp || rec.originalFilename || '';
}

export function matchCandidates(
  csvRow: SunfireRowFull,
  recordings: RecordingRecord[],
): RecordingRecord[] {
  const csvPhone = normalizePhone(csvRow.applicantPhone);
  if (!csvPhone) return [];
  return recordings
    .filter((rec) => {
      const recPhone = extractPhoneFromRecording(rec);
      const phoneMatch = csvPhone === recPhone || csvPhone.slice(-10) === recPhone.slice(-10);
      const dateMatch = !csvRow.date || csvRow.date === rec.recordDate;
      return phoneMatch && dateMatch;
    })
    .sort((a, b) => recordingSortKey(a).localeCompare(recordingSortKey(b)));
}

export function buildMatchPlan(
  csvRows: SunfireRowFull[],
  recordings: RecordingRecord[],
): SmsMatchPlan {
  const auto: SmsMatchedPair[] = [];
  const groups: MultiCallGroup[] = [];
  const unmatchedCsv: SunfireRowFull[] = [];
  const usedKeys = new Set<string>();

  for (const csvRow of csvRows) {
    const candidates = matchCandidates(csvRow, recordings);
    if (candidates.length === 0) {
      unmatchedCsv.push(csvRow);
    } else if (candidates.length === 1) {
      auto.push({ csvRow, recording: candidates[0], matchType: 'auto' });
      usedKeys.add(candidates[0].s3Key);
    } else {
      groups.push({ csvRow, candidates });
      candidates.forEach((c) => usedKeys.add(c.s3Key));
    }
  }
  const unmatchedRecordings = recordings.filter((r) => !usedKeys.has(r.s3Key));
  return { auto, groups, unmatchedCsv, unmatchedRecordings };
}
